import { Suspense, useEffect, useRef, useState, type ComponentType, type PropsWithChildren } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider as ReactAriaRouterProvider } from 'react-aria-components';
import { BrowserRouter, useLocation, useNavigate, useRoutes } from 'react-router';
import { GlobalErrorReporter } from './GlobalErrorReporter';
import { RegistrationGate } from '../features/session/RegistrationGate';
import type { NotificationCountGateway } from '../features/notifications/notification-count-gateway';
import { SessionGate } from '../features/session/SessionGate';
import { SessionProvider, useSession } from '../features/session/SessionContext';
import { RealtimeSync } from '../features/realtime/RealtimeSync';
import { PresenceProvider } from '../features/presence/PresenceProvider';
import { ActivityTracker } from '../services/activity/ActivityTracker';
import type { SessionGateway } from '../services/appwrite/session-gateway';
import { createQueryClient } from '../services/query/query-client';
import { AnnouncerProvider } from '../shared/accessibility/Announcer';
import { DocumentTitle } from '../shared/accessibility/DocumentTitle';
import { ToastProvider } from '../shared/accessibility/Toast';
import { AudioProvider } from '../shared/audio/AudioProvider';
import { useAppliedPreferences } from '../shared/accessibility/use-applied-preferences';
import { SiteFooter } from '../shared/layout/SiteFooter';
import { SiteHeader } from '../shared/layout/SiteHeader';
import { isPublicPath, routes } from './routes';

interface AppProps {
  gateway?: SessionGateway;
  notificationGateway?: NotificationCountGateway;
  /** Router alternativo. Las pruebas pasan `MemoryRouter` para fijar la ruta. */
  router?: ComponentType<PropsWithChildren>;
}

function AppRoutes() {
  return useRoutes(routes());
}

/**
 * Lleva el foco al `<h1>` de la página nueva tras cada navegación, y también
 * en la primera carga: así el lector de pantalla anuncia ese encabezado
 * («Rol Sensorial», «Entra a Rol Sensorial», etc.) en vez de quedarse con lo
 * primero que le llegue a leer de la página, sea lo que sea.
 *
 * La página puede tardar en llegar (carga diferida con `Suspense`, o una
 * `SessionGate` esperando la sesión), así que no alcanza con enfocar apenas
 * cambia la ruta: hay que esperar a que el encabezado aparezca de verdad. Un
 * `MutationObserver` cubre eso sin acoplar este componente a cada pantalla.
 *
 * En la primera carga, enfocar de inmediato no llega a anunciarse: el lector
 * de pantalla todavía está procesando el documento recién llegado. Un
 * pequeño retardo le da tiempo antes de mover el foco.
 *
 * El `<main>` recibe mutaciones que no tienen nada que ver con la
 * navegación —las publicaciones de un tablón que terminan de cargar, un
 * contador que llega, mensajes de un chat—, así que el observador no puede
 * reaccionar a cualquier cambio: compara el propio nodo `<h1>` con el último
 * que vio. Si sigue siendo el mismo elemento, no hay nada que enfocar de
 * nuevo, sin importar qué otra cosa haya mutado alrededor.
 *
 * La primera vez que aparece un `<h1>` en la ruta nueva (todavía no había
 * ninguno) se enfoca sin condiciones: es lo que hace que cambiar de pantalla
 * lleve el foco ahí. Al hacer clic en un enlace, por ejemplo, el foco
 * todavía está en ese enlace, no en `<body>` ni en nada que este hook haya
 * puesto — exigir eso como condición para el primer enfoque haría que nunca
 * se moviera.
 *
 * Si en cambio el `<h1>` cambia por otro (una pantalla que reemplaza su
 * encabezado provisorio, «Cargando…», por el definitivo cuando llega el
 * dato —la ficha de una publicación, por ejemplo—), sí hace falta la
 * condición: solo se mueve el foco si todavía está en el encabezado
 * anterior (o se perdió a `<body>` porque ese nodo se sacó del documento) —
 * así, si la persona ya movió el foco a otro lado por su cuenta después de
 * aterrizar, no se lo robamos.
 *
 * En la primera carga, el observador no arranca hasta que pasa ese mismo
 * retardo de un segundo: si arrancara antes, alcanzaría a ver aparecer el
 * `<h1>` de la propia carga inicial —tarde, en cualquier momento dentro de
 * ese primer segundo, según cuánto demore la sesión o la carga diferida— y
 * lo enfocaría igual, aunque para entonces la persona ya haya movido el
 * foco a otra parte por su cuenta (a un enlace de la cabecera, por ejemplo).
 */
function useFocusMainHeadingOnNavigate(pathname: string) {
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const container = document.getElementById('main-content');
    if (!container) return;

    let currentHeading: HTMLElement | undefined;
    let lastFocused: HTMLElement | undefined;

    const focusHeading = (heading: HTMLElement | undefined) => {
      const target = heading ?? container;
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus();
      lastFocused = target;
      currentHeading = heading;
    };

    const maybeFocusHeading = () => {
      const heading = container.querySelector('h1') ?? undefined;
      if (!heading || heading === currentHeading) return;
      if (currentHeading) {
        const active = document.activeElement;
        if (active !== document.body && active !== lastFocused) return;
      }
      focusHeading(heading);
    };

    const observer = new MutationObserver(maybeFocusHeading);
    observer.observe(container, { childList: true, subtree: true });
    maybeFocusHeading();

    return () => {
      observer.disconnect();
    };
  }, [pathname]);
}

/** Hace que los enlaces de React Aria usen el enrutador sin recargar el sitio. */
function AccessibleRouterProvider({ children }: PropsWithChildren) {
  const navigate = useNavigate();
  return (
    <ReactAriaRouterProvider navigate={(href) => void navigate(href)}>
      {children}
    </ReactAriaRouterProvider>
  );
}

/**
 * Decide entre el registro y la aplicación normal.
 *
 * Una cuenta autenticada sin fila de perfil todavía no puede usar el sitio:
 * ve **solo** el formulario de registro, sin cabecera ni rutas, para que no
 * pueda navegar a ningún lado sin completarlo. La señal (`profile.exists`) es
 * la misma para las tres superficies, porque el perfil es del sitio entero,
 * no de `app.rolsensorial.com` en particular.
 */
function AppShell({
  notificationGateway,
}: {
  notificationGateway?: NotificationCountGateway;
}) {
  const { state } = useSession();
  const location = useLocation();
  useFocusMainHeadingOnNavigate(location.pathname);

  if (state.status === 'authenticated' && !state.profile.profile.exists) {
    return <RegistrationGate />;
  }

  return (
    <>
      <DocumentTitle />
      <a className="visually-hidden-focusable skip-link" href="#main-content">
        Saltar al contenido principal
      </a>
      <SiteHeader notificationGateway={notificationGateway} />
      <main id="main-content" className="container py-4" tabIndex={-1}>
        <SessionGate allowAnonymous={isPublicPath(location.pathname)}>
          <Suspense fallback={<p className="loading-surface" role="status">Cargando tu espacio…</p>}>
            <AppRoutes />
          </Suspense>
        </SessionGate>
      </main>
      <hr className="site-footer-divider mt-5 mb-0" />
      <SiteFooter />
    </>
  );
}

export default function App({ gateway, notificationGateway, router: Router = BrowserRouter }: AppProps) {
  const [queryClient] = useState(createQueryClient);
  useAppliedPreferences();

  return (
    <AnnouncerProvider>
      <ToastProvider>
        <GlobalErrorReporter />
        <QueryClientProvider client={queryClient}>
          <AudioProvider>
            <SessionProvider gateway={gateway}>
              <Router>
                <AccessibleRouterProvider>
                  <PresenceProvider>
                    <RealtimeSync notificationGateway={notificationGateway} />
                    <ActivityTracker />
                    <AppShell notificationGateway={notificationGateway} />
                  </PresenceProvider>
                </AccessibleRouterProvider>
              </Router>
            </SessionProvider>
          </AudioProvider>
        </QueryClientProvider>
      </ToastProvider>
    </AnnouncerProvider>
  );
}
